import type { RecordRow } from './api';
// W5 — the pack's own type and relationship keys, from the ONE place the web
// keeps them. Not re-typed here: packLinkKeys.test.ts checks that object
// against the pack's declarations line by line, and a copy it does not check
// is a copy that can drift (a wrong link key renders an empty list, forever).
import { AA, LINK } from './auditionSpread';
import { disciplineOf, humanizeDepartment } from './discipline';
import { humanizeValue } from './fieldDisplay';

// A10 — THE RESUME, ASSEMBLED FROM WORK THAT ACTUALLY HAPPENED.
//
// ===================================================================
// THE DESIGN LAW OF THIS MODULE
// ===================================================================
// "A credit is not a thing you write. It is a thing you did, said back to
// you." Decision 822d959e: A CREDIT IS AN ENGAGEMENT.
//
// The old engine on the frozen actor pack inferred a credit from a project's
// lifecycle OR from a booked audition — two sources, one of them a guess.
// Here there is one: the engagement is the record that means somebody
// actually worked, it carries the season and the block dates, and it is the
// same record the shoot days and the payments already hang off.
//
// SO NOTHING HERE CAN BE TYPED. There is no "add a credit" anywhere in this
// tranche, and that is the point: a credit that can be typed can disagree
// with the work, and the first time it does the product is lying on the one
// page an actor hands to a stranger.
//
// ===================================================================
// ONE ASSEMBLY, TWO GROUPING AXES
// ===================================================================
// Cast credits group by the PRODUCTION'S KIND; crew credits group by the
// JOB'S DEPARTMENT. Same credits, same sort, same renderer — the axis is
// chosen by the role's discipline, which is the single fact A9 exists to
// provide. A career that holds both kinds of work produces both resumes from
// one set of records, which is what "track separately enough so we can
// properly prepare 2 types of resumes" (Huan, 2026-08-22) asks for.

/** The engagement states that mean the work happened or is happening.
 *
 * `wrapped` is unambiguous. `shooting` and `contracted` are real work under
 * way and belong on a resume an actor sends this week — an actor three days
 * into a shoot does not have a blank resume.
 *
 * `offered` and `accepted` are NOT credits: an offer is not a job, and a
 * resume that lists offers is a resume nobody can trust. `cancelled` never
 * was one. */
export const CREDIT_STATES = ['contracted', 'shooting', 'wrapped'] as const;

export function isCredit(engagement: RecordRow): boolean {
  return (CREDIT_STATES as readonly string[]).includes(engagement.lifecycle_state ?? '');
}

/** The canonical cast groups, in the order a resume prints them. */
export const CAST_GROUPS = ['Film', 'Television', 'Theatre', 'Commercial', 'Other'] as const;
export type CastGroup = (typeof CAST_GROUPS)[number];

/** `aa_project.kind` → the group it prints under. A production with no kind
 * recorded lands in Other rather than being dropped: the work happened, and
 * a missing field on the production is not a reason to lose the credit. */
export function castGroupFor(project: RecordRow | null): CastGroup {
  const kind = project && typeof project.fields.kind === 'string' ? project.fields.kind : '';
  switch (kind) {
    case 'feature':
    case 'short':
      return 'Film';
    case 'series':
    case 'limited_series':
    case 'pilot':
      return 'Television';
    case 'theatre':
      return 'Theatre';
    case 'commercial':
      return 'Commercial';
    default:
      return 'Other';
  }
}

/** The crew group is the department, humanised. A crew job with no
 * department recorded groups under "Other" — NAMED, not dropped, for the
 * same reason: losing a credit is worse than filing it loosely, and the
 * person can see the loose one and fix it. */
export function crewGroupFor(role: RecordRow | null): string {
  const dept = role && typeof role.fields.department === 'string' ? role.fields.department.trim() : '';
  return dept ? humanizeDepartment(dept) : 'Other';
}

export interface Credit {
  engagementId: string;
  /** 'cast' | 'crew' — which resume this belongs on. */
  side: 'cast' | 'crew';
  group: string;
  production: string | null;
  /** The character, or the crew job — TIERED AND QUOTED when the role
   * records a tier (`aa_role.size`): "Guest Star — "Dockhand Pell"", the
   * same convention an old-resume import's own `character_name` already
   * carries baked in as one string (B19 §3). Bare, unquoted, when there is
   * no tier — never invented either way. */
  role: string | null;
  company: string | null;
  /** Present only when the production is episodic AND a season was recorded —
   * "Season 2" on a feature is nonsense. */
  season: number | null;
  /** What the sort runs on. ISO, always present: block_start when known,
   * else the engagement's own creation, which is the only other date this
   * record is guaranteed to have. NEVER printed — see `year` for what a
   * reader is shown. */
  when: string;
  /** B19 §3 — the year PRINTED beside the credit: `block_end`, else
   * `block_start`, else the production's own printed year (the only date an
   * OLD-RESUME import carries), else null. Never the engagement's
   * `created_at` — that is a fact about this database's bookkeeping, not
   * about the job, and printing it as "the year" is an invented date. */
  year: number | null;
}

export interface CreditGroup {
  group: string;
  credits: Credit[];
  /** Which half of the career this group belongs to. Carried on the group
   * because the EVERYTHING variant needs it and the single-side variants do
   * not: "Film" above "Lighting" with nothing between them reads as one list
   * of five departments, which is the one thing that variant exists to
   * avoid. */
  side: 'cast' | 'crew';
}

/** Everything one assembly needs. Passed in rather than fetched so this
 * module stays pure and testable without a browser — the same discipline
 * farSide.ts and selfTape.ts follow. */
export interface ResumeInput {
  engagements: RecordRow[];
  /** Every role and project, by id. A credit whose role or project is
   * missing is DROPPED — see assembleCredits. */
  rolesById: Map<string, RecordRow>;
  projectsById: Map<string, RecordRow>;
  /** engagement id → the role it is for. */
  roleOf: (engagementId: string) => string | null;
  /** role id → the project it is on. */
  projectOf: (roleId: string) => string | null;
}

/** Assemble every credit, unsorted and ungrouped.
 *
 * ⚑ A CREDIT WITH NO ROLE IS DROPPED, NOT RENDERED HALF-BLANK. A resume line
 * that reads "— , Paramount" is worse than one credit fewer: it makes the
 * whole page look unfinished on the page where that matters most. The role is
 * the one part with no substitute; a missing PROJECT still leaves the role
 * readable, so that one survives with a null production. */
export function assembleCredits(input: ResumeInput): Credit[] {
  const out: Credit[] = [];
  for (const eng of input.engagements) {
    if (!isCredit(eng)) continue;
    const roleId = input.roleOf(eng.id);
    const role = roleId ? input.rolesById.get(roleId) ?? null : null;
    if (!role) continue;
    const projectId = input.projectOf(role.id);
    const project = projectId ? input.projectsById.get(projectId) ?? null : null;

    const side = disciplineOf(role) === 'crew' ? 'crew' : 'cast';
    const episodic = project?.fields.episodic === true;
    const seasonRaw = eng.fields.season;
    const characterName =
      typeof role.fields.character_name === 'string' ? role.fields.character_name : role.display_name;
    out.push({
      engagementId: eng.id,
      side,
      group: side === 'cast' ? castGroupFor(project) : crewGroupFor(role),
      production: project && typeof project.fields.title === 'string' ? project.fields.title : null,
      role: creditRoleLine(characterName, role.fields.size),
      company: project && typeof project.fields.company === 'string' && project.fields.company.trim()
        ? project.fields.company
        : null,
      season: episodic && typeof seasonRaw === 'number' && Number.isFinite(seasonRaw) ? seasonRaw : null,
      when: whenOf(eng, project),
      year: creditYear(eng, project),
    });
  }
  return out;
}

/** B19 §3 — the character/job name, tiered and quoted when the role
 * records one: "Guest Star — "Dockhand Pell"", the exact form an old-resume
 * import's own `character_name` text already carries baked in as one
 * string. `aa_role.size` is the pack's real billing/tier field (labelled
 * "size" on the record, described as "HOW BIG THE PART IS" —
 * recordTypes.ts). Bare when there is no tier — never invented either
 * way, and never double-applied: an imported credit's `character_name` is
 * whatever free text the document printed, which this never re-wraps. */
function creditRoleLine(characterName: string, sizeRaw: unknown): string {
  const tier = typeof sizeRaw === 'string' && sizeRaw.trim() ? humanizeValue(sizeRaw.trim()) : null;
  // Atlas review (live data, 2026-09-26): an IMPORTED résumé line already
  // carries its tier in the name — `Recurring — "Officer Tam"` with size
  // `recurring`. Tiering it again printed the tier twice. A name that already
  // leads with the tier, or is already quoted, is left exactly as written.
  if (tier && (characterName.toLowerCase().startsWith(tier.toLowerCase()) || characterName.includes('"'))) return characterName;
  return tier ? `${tier} — "${characterName}"` : characterName;
}

/** B19 §3 — the year PRINTED beside a credit: the engagement's own
 * `block_end`, else `block_start` (the pack's real dates — never available
 * to an old-resume import, which carries no dates at all), else the
 * production's own printed year (the only date an import DOES carry, W4),
 * else null. Never the engagement's `created_at` — see `Credit.year`'s own
 * comment for why that fallback (still used by `when`, the SORT key, which
 * must always land somewhere) is never a fact to PRINT. */
function creditYear(eng: RecordRow, project: RecordRow | null): number | null {
  const yearOfDate = (v: unknown): number | null => {
    if (typeof v !== 'string' || !v.trim()) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.getUTCFullYear();
  };
  const projectYear = project?.fields.year;
  return (
    yearOfDate(eng.fields.block_end) ??
    yearOfDate(eng.fields.block_start) ??
    (typeof projectYear === 'number' && Number.isInteger(projectYear) && projectYear >= 1900 && projectYear <= 2200
      ? projectYear
      : null)
  );
}

/** block_start if it parses, else the PRODUCTION'S PRINTED YEAR, else the
 * engagement's creation. Never NaN — an unsortable credit would land wherever
 * the sort happened to leave it, which reads as random to the only person who
 * knows the real order.
 *
 * ⚑ THE PROJECT-YEAR RUNG IS W4's (decision `7c04876f`). An old resume states
 * a year and never a date, so an imported credit legitimately carries no
 * `block_start` — without this rung the SORT ORDER would fall straight
 * through to `created_at` and place a fifteen-year-old theatre credit as if
 * it had happened on the afternoon it was imported.
 *
 * ⚠ B19 §3 — THIS IS THE SORT KEY ONLY, NOT WHAT IS PRINTED (as it used to
 * be, through `creditLine`'s own `getFullYear()`). A credit MUST sort
 * somewhere even with no real date recorded at all, so this still falls all
 * the way to `created_at` — but printing that same fallback as "the year"
 * was an invented fact about this database's bookkeeping, not the job. What
 * a reader is SHOWN is `Credit.year` (`creditYear` below), which stops at
 * the production's printed year and goes no further. */
function whenOf(eng: RecordRow, project: RecordRow | null): string {
  const raw = eng.fields.block_start;
  if (typeof raw === 'string' && raw.trim() && !Number.isNaN(new Date(raw).getTime())) {
    return new Date(raw).toISOString();
  }
  const year = project?.fields.year;
  // A four-digit year, and nothing looser: `Number('2024ish')` is NaN, but a
  // stray `20` would silently anchor a credit in the year 20.
  if (typeof year === 'number' && Number.isInteger(year) && year >= 1900 && year <= 2200) {
    return new Date(Date.UTC(year, 0, 1)).toISOString();
  }
  return eng.created_at;
}

/** Newest first inside a group — the resume convention, and the same rule
 * the old engine landed on. Three-way compare so equal dates return 0 rather
 * than reordering on every render. */
function newestFirst(a: Credit, b: Credit): number {
  return a.when < b.when ? 1 : a.when > b.when ? -1 : 0;
}

/** The cast resume: canonical group order, empty groups omitted. */
export function castResume(credits: Credit[]): CreditGroup[] {
  return CAST_GROUPS.map((g) => ({
    group: g,
    side: 'cast' as const,
    credits: credits.filter((c) => c.side === 'cast' && c.group === g).sort(newestFirst),
  })).filter((g) => g.credits.length > 0);
}

/** The crew resume: departments in alphabetical order, with Other last
 * wherever it falls. There is no canonical department order on a resume the
 * way there is for cast media, and inventing one would be a claim about
 * seniority nobody asked for. */
export function crewResume(credits: Credit[]): CreditGroup[] {
  const groups = new Map<string, Credit[]>();
  for (const c of credits) {
    if (c.side !== 'crew') continue;
    const list = groups.get(c.group) ?? [];
    list.push(c);
    groups.set(c.group, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
    .map(([group, list]) => ({ group, side: 'crew' as const, credits: list.sort(newestFirst) }));
}

export type ResumeVariantKey = 'cast' | 'crew' | 'everything';

export interface ResumeVariant {
  key: ResumeVariantKey;
  label: string;
  groups: CreditGroup[];
}

/** Which resumes this career actually has.
 *
 * ⚑ A VARIANT WITH NO CREDITS IS NOT OFFERED. An empty "Crew" tab on an
 * actor who has never crewed is an invitation to wonder what they did wrong,
 * and "Everything" beside a career with only one kind of work is the same
 * page twice under two names. What is offered is what exists. */
export function resumeVariants(credits: Credit[]): ResumeVariant[] {
  const cast = castResume(credits);
  const crew = crewResume(credits);
  const out: ResumeVariant[] = [];
  if (cast.length > 0) out.push({ key: 'cast', label: 'Cast', groups: cast });
  if (crew.length > 0) out.push({ key: 'crew', label: 'Crew', groups: crew });
  if (cast.length > 0 && crew.length > 0) {
    out.push({ key: 'everything', label: 'Everything', groups: [...cast, ...crew] });
  }
  return out;
}

/** The line under a credit's name: company, season, year — whichever exist,
 * in that order, joined once. Absent pieces leave no separator behind. */
export function creditLine(credit: Credit): string {
  const bits: string[] = [];
  if (credit.company) bits.push(credit.company);
  if (credit.season !== null) bits.push(`Season ${credit.season}`);
  if (credit.year !== null) bits.push(String(credit.year));
  return bits.join(' · ');
}

// ===================================================================
// W5 — THE TYPED BLOCKS: TRAINING, SPECIAL SKILLS, AWARDS.
//
// Everything above this line is assembled from work that happened, and the
// module header's law still holds for it: a credit cannot be typed. These
// three cannot be anything ELSE.
//
// A class nobody booked you for, a prize a jury gave you and the fact that
// you ride horses are not records of work — they have no engagement, no
// project and no role to derive from, and there is nowhere in this app they
// could arrive from except a person saying so. `aa_training`, `aa_award` and
// `aa_skill` are the pack's three types for exactly that (recordTypes.ts,
// "the manual resume blocks"), and until W5 they had no screen at all: they
// sat behind the Development registry tab and never reached the one page
// they exist for.
//
// So this half of the file is the opposite discipline from the first half,
// on purpose, and the seam between them is the point: the resume is
// ASSEMBLED where it can be and TYPED where it cannot, and a reader can tell
// which is which by which section they are looking at.
//
// ⚑ UNGATED ON EVERY TIER (plan of record §2, W5's ruling). What a paid plan
// buys is the resume EDITOR and the old-resume IMPORT — never the right to
// type your own history. There is no requireFeature/planHas anywhere in this
// block or in the screen that renders it.
//
// SHAPE. One row per RECORD, three columns, the same grid the credit table
// already uses — so the sheet's typography is reused rather than re-derived,
// and so every line on the page has an id behind it. That last part is not
// cosmetic: the edit and retire doors are per-row, and a row synthesised out
// of several records (the printed convention of collapsing skills into
// "Dance: ballet, tap") would be a line with nothing to open.

/** Which block. The screen's own vocabulary — the pack's type key rides
 * alongside it rather than being parsed back out of this. */
export type ResumeBlockKey = 'training' | 'skills' | 'awards';

export interface ResumeBlockRow {
  /** The record behind the line. The edit door opens THIS. */
  id: string;
  /** Left column. Null only where the pack allows it (a skill's category);
   * training and awards both make their lead field required. */
  lead: string | null;
  /** Middle column — the qualifying facts, in the pack's own field order,
   * joined once. Absent pieces leave no separator behind, the same rule
   * creditLine() follows. */
  middle: string | null;
  /** Right column — when, or how it went. Tabular, like a credit's year. */
  trail: string | null;
}

export interface ResumeBlock {
  key: ResumeBlockKey;
  /** The pack's type key — what the add door creates and the edit door opens. */
  typeKey: string;
  /** The relationship key that points the record at its performer, so a
   * block created while standing in one performer's resume is ABOUT them. */
  subjectLink: string;
  /** The heading the DOCUMENT uses. "Special skills" is the industry's word
   * for aa_skill's block and the word the frozen actor pack's own printed
   * resume used; the pack's `label_plural` ("Skills") is a registry label,
   * which is a different job. Training and Awards are the pack's own. */
  label: string;
  /** What the ADD DOOR says. Written out rather than derived from `label`:
   * "Training" un-pluralises to "training" and "Awards" to "award", which is
   * two different sentences from one rule, and English has no rule that gets
   * "Add a skill" out of "Special skills" at all. */
  addLabel: string;
  rows: ResumeBlockRow[];
}

/** A field as trimmed text, or null. Anything non-string (a number year, a
 * null) is not text and is handled where it means something. */
function text(rec: RecordRow, name: string): string | null {
  const v = rec.fields[name];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** The pieces that exist, joined once. Empty → null, never an empty string:
 * a cell that renders "" and a cell that renders nothing look the same, and
 * only one of them is honest about there being no middle column. */
function joined(parts: (string | null)[]): string | null {
  const kept = parts.filter((p): p is string => p !== null && p !== '');
  return kept.length > 0 ? kept.join(' · ') : null;
}

/** `stunt_driving` → `Stunt driving`. Same shape humanizeDepartment uses for
 * the crew groups, without its named exceptions — a skill category is free
 * text a person typed, so there is no vocabulary to special-case. */
function humanizeWord(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** The YEAR off a date field, WITHOUT constructing a Date.
 *
 * ⚑ DELIBERATE. `new Date('2019-09-01').getFullYear()` is parsed as UTC and
 * read back in the local zone — west of Greenwich that is 2019-08-31, and a
 * class that started in January prints as the year before. The pack's `date`
 * fields are calendar dates, not instants; the first four digits ARE the
 * year and nothing about a timezone can improve on them. */
function yearOf(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value !== 'string') return null;
  const m = /^\s*(\d{4})/.exec(value);
  return m ? Number(m[1]) : null;
}

/** "2019–2021", "2019", or null. A single year when the two agree, because
 * "2019–2019" is a typo the page would be repeating back. An open span is
 * printed as its start alone: a class with a start and no end is a class
 * that may be over and may not, and "2019–" is a claim about which. */
function span(from: unknown, to: unknown): string | null {
  const a = yearOf(from);
  const b = yearOf(to);
  if (a !== null && b !== null) return a === b ? String(a) : `${a}–${b}`;
  return a !== null ? String(a) : b !== null ? String(b) : null;
}

/** Newest first, the resume's rule everywhere. Records with no date at all
 * fall to the BOTTOM rather than the top — an undated row jumping ahead of a
 * dated one reads as the most recent thing the person did, which is a claim
 * nobody made. */
function byRecency<T>(rows: T[], keyOf: (row: T) => number | null): T[] {
  return [...rows].sort((x, y) => {
    const a = keyOf(x);
    const b = keyOf(y);
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b - a;
  });
}

/** aa_training, verbatim from the pack: name (required) · institution ·
 * discipline · instructor · started_on · ended_on · notes.
 *
 * `notes` IS NOT PRINTED, here or in either block below. It is the field the
 * pack gives a person for what they thought of the class — a private note on
 * their own record — and a resume is the one page that goes to a stranger.
 * It is fully readable and editable in the record editor the rows open. */
function trainingRows(records: RecordRow[]): ResumeBlockRow[] {
  return byRecency(records, (r) => yearOf(r.fields.ended_on) ?? yearOf(r.fields.started_on)).map(
    (r) => ({
      id: r.id,
      lead: text(r, 'name') ?? r.display_name,
      middle: joined([text(r, 'institution'), text(r, 'discipline'), text(r, 'instructor')]),
      trail: span(r.fields.started_on, r.fields.ended_on),
    })
  );
}

/** The picklist's own words, said the way a resume says them. `other` prints
 * NOTHING: it is the pack's escape hatch for a result that is neither a
 * nomination nor a win, and "Other" beside an award is a word that means
 * less than the silence it replaced. */
function awardResult(value: unknown): string | null {
  return value === 'won' ? 'Won' : value === 'nominated' ? 'Nominated' : null;
}

/** aa_award, verbatim: title (required) · awarding_body · category · year ·
 * result (nominated|won|other) · notes. */
function awardRows(records: RecordRow[]): ResumeBlockRow[] {
  return byRecency(records, (r) => yearOf(r.fields.year)).map((r) => ({
    id: r.id,
    lead: text(r, 'title') ?? r.display_name,
    middle: joined([text(r, 'awarding_body'), text(r, 'category')]),
    trail: joined([awardResult(r.fields.result), yearOf(r.fields.year)?.toString() ?? null]),
  }));
}

/** aa_skill, verbatim: name (required) · category · proficiency
 * (basic|intermediate|advanced|professional) · notes.
 *
 * Sorted by CATEGORY then name, so the dialects sit together and the sports
 * sit together — the grouping a printed resume gets from headings, without
 * synthesising a row that no record stands behind. An uncategorised skill
 * sorts LAST, which is the same "Other last" rule crewResume() follows. */
function skillRows(records: RecordRow[]): ResumeBlockRow[] {
  const keyed = records.map((r) => ({
    record: r,
    category: text(r, 'category'),
    name: text(r, 'name') ?? r.display_name,
  }));
  keyed.sort((a, b) => {
    if (a.category !== b.category) {
      if (a.category === null) return 1;
      if (b.category === null) return -1;
      return a.category.localeCompare(b.category);
    }
    return a.name.localeCompare(b.name);
  });
  return keyed.map(({ record, category, name }) => ({
    id: record.id,
    lead: name,
    middle: category ? humanizeWord(category) : null,
    trail: typeof record.fields.proficiency === 'string' && record.fields.proficiency.trim()
      ? humanizeWord(record.fields.proficiency.trim())
      : null,
  }));
}

export interface ResumeBlockInput {
  training: RecordRow[];
  skills: RecordRow[];
  awards: RecordRow[];
}

/** WHAT each block is, said once. The shaping functions above turn records
 * into rows; this is everything else the screen needs to know about a block —
 * which pack type it holds, which relationship makes a record ABOUT the
 * performer whose resume this is, and what the document calls it.
 *
 * IT IS EXPORTED WHOLE, empty rows and all, because the ADD DOORS ARE BUILT
 * FROM IT rather than from the rendered blocks. A block with nothing in it
 * does not print (see resumeBlocks below) — but the person with no training
 * at all is exactly the one who needs somewhere to put their first class, and
 * a door that disappears when the shelf is empty is the shape of "you can
 * only add a second one". */
export const RESUME_BLOCKS: readonly Omit<ResumeBlock, 'rows'>[] = [
  {
    key: 'training',
    typeKey: AA.TRAINING,
    subjectLink: LINK.TRAINING_SUBJECT,
    label: 'Training',
    addLabel: 'Add training',
  },
  {
    key: 'skills',
    typeKey: AA.SKILL,
    subjectLink: LINK.SKILL_SUBJECT,
    label: 'Special skills',
    addLabel: 'Add a skill',
  },
  {
    key: 'awards',
    typeKey: AA.AWARD,
    subjectLink: LINK.AWARD_SUBJECT,
    label: 'Awards',
    addLabel: 'Add an award',
  },
] as const;

const SHAPERS: Record<ResumeBlockKey, (input: ResumeBlockInput) => ResumeBlockRow[]> = {
  training: (i) => trainingRows(i.training),
  skills: (i) => skillRows(i.skills),
  awards: (i) => awardRows(i.awards),
};

/**
 * The three blocks, in the order a resume prints them: TRAINING, then
 * SPECIAL SKILLS, then AWARDS, all of it after the credits.
 *
 * ⚑ THE ORDER IS THE CONVENTION, AND IT IS NOT ARBITRARY. Credits are what
 * the page is for; training is the answer to "and where did that come from";
 * skills are what a casting director scans for a specific ask; awards are the
 * flourish. On many printed resumes the awards ride INSIDE the training
 * block — this file keeps them separate because they are separate records
 * with separate fields, and collapsing them would mean a prize could not be
 * edited without opening a class.
 *
 * AN EMPTY BLOCK IS NOT RETURNED AT ALL. A heading over nothing is the same
 * defect resumeVariants() refuses one section up: a "Special skills" rule
 * with white space under it on the page an actor hands to a stranger reads
 * as something they failed to fill in.
 */
export function resumeBlocks(input: ResumeBlockInput): ResumeBlock[] {
  return RESUME_BLOCKS.map((def) => ({ ...def, rows: SHAPERS[def.key](input) })).filter(
    (b) => b.rows.length > 0
  );
}
